/**
 * ENG-9353 — redact known secret shapes from pane.log AT REST.
 *
 * `pane.log` is a raw tmux stream (`tmux pipe-pane`), so anything echoed to the
 * terminal lands on disk verbatim — an agent running `env`, a debug line
 * printing a `Bearer` header, a mis-logged bot token — entirely outside the
 * 0600 secret-file discipline that governs `.env.integrations` and friends. The
 * platform-admin *read* surfaces already scrub pane content with
 * `@augmented/core`'s `redactSecrets`, but that leaves the on-disk file (and its
 * S3 archives, ENG-8340/8438) in cleartext. This module builds the streaming
 * redaction sink that scrubs those tokens BEFORE they reach the file, using the
 * exact same canonical pattern set — so at-rest and read-time redaction can
 * never disagree about what a secret looks like.
 *
 * Design constraints (each learned the hard way):
 *
 *  1. **Preserve mtime freshness.** Several consumers read pane.log's mtime as
 *     the agent-idle proxy (`pane-occupancy-sampler`, the responsiveness
 *     probe). A line-buffered filter (`sed -u` / `awk` / `perl -p`) holds
 *     output until a newline — and Claude's TUI redraws with `\r`, not `\n`, so
 *     an active-but-newline-quiet pane would read as idle. The perl sink below
 *     copies stdin→stdout in raw 64 KB blocks (`sysread`/`syswrite`, `$| = 1`),
 *     flushing exactly as often as the `cat` it replaces.
 *
 *  2. **Fail safe.** If perl is unavailable the caller falls back to plain
 *     `cat` (today's behaviour). A diagnostic redactor must never be the reason
 *     pane capture — and with it fleet-wide idle detection — stops.
 *
 *  3. **Single source of truth.** The rules come straight from
 *     `@augmented/core`'s `SECRET_PATTERNS`; there is no parallel list to drift.
 *     The JS regex sources translate 1:1 to perl (character classes, `\b`,
 *     non-capturing groups, `\s`, `{n,}` — no JS-only constructs), and the
 *     replacement mirrors `redactSecrets`' `<redacted:label>` form.
 *
 * Known bound: a secret split across a 64 KB read boundary is not redacted. A
 * single echoed credential is one contiguous write far short of 64 KB, so this
 * is not a realistic leak path. It is documented rather than closed with a
 * carry-over buffer, which would delay the trailing bytes of each block and
 * dull the very mtime signal constraint (1) exists to protect.
 */

import { SECRET_PATTERNS } from '@augmented/core';

/**
 * Build the perl program that streams stdin→stdout applying every canonical
 * secret pattern. Written to a script file (not inlined into the tmux sink) so
 * the program's own quotes and `$` sigils never collide with the shell/tmux
 * quoting around the sink command.
 *
 * Each `SECRET_PATTERNS` entry becomes one `s/<source>/<redacted:label>/g`
 * line. `/` is a safe delimiter: no canonical pattern or label contains one.
 */
export function buildRedactorProgram(
  patterns: readonly { re: RegExp; label: string }[] = SECRET_PATTERNS,
): string {
  const subs = patterns
    .map((p) => `  $buf =~ s/${p.re.source}/<redacted:${p.label}>/g;`)
    .join('\n');
  return [
    '#!/usr/bin/perl',
    '# ENG-9353 pane.log secret redactor — GENERATED by pane-log-redactor.ts from',
    "# @augmented/core's SECRET_PATTERNS. Do not edit here; change the canonical",
    '# list and respawn. Streams stdin to stdout in raw 64KB blocks so pane.log',
    '# mtime stays as fresh as the `cat` it replaces.',
    'use strict; use warnings;',
    'use Errno qw(EINTR);',
    '$| = 1;',
    'binmode(STDIN); binmode(STDOUT);',
    'my $buf;',
    // Retry interrupted syscalls, exactly as the `cat` this replaces does. A
    // signal during the blocking sysread makes it return undef with $! == EINTR;
    // perl does NOT auto-restart, so `undef > 0` would end the loop, the sink
    // would exit, and pane capture (with the mtime idle signal) would stop for
    // the rest of the session. Same for a partial/interrupted syswrite.
    'OUTER: while (1) {',
    '  my $n = sysread(STDIN, $buf, 65536);',
    '  if (!defined $n) { next OUTER if $! == EINTR; last OUTER; }',
    '  last OUTER if $n == 0;',
    subs,
    '  my $off = 0;',
    '  while ($off < length($buf)) {',
    '    my $w = syswrite(STDOUT, $buf, length($buf) - $off, $off);',
    '    unless (defined $w) { next if $! == EINTR; last OUTER; }',
    '    $off += $w;',
    '  }',
    '}',
    '',
  ].join('\n');
}

/**
 * Build the `tmux pipe-pane` sink command that appends (redacted) pane output
 * to `logPath`.
 *
 * - With perl available: `perl '<script>' >> '<log>'` — the streaming redactor.
 * - Without perl: `cat >> '<log>'` — the pre-ENG-9353 behaviour, so pane
 *   capture (and the idle detection that rides on it) never depends on the
 *   redactor being installable.
 *
 * Both paths single-quote their arguments for the shell tmux runs, matching the
 * existing sink's quoting.
 */
export function buildPaneSinkCommand(opts: {
  logPath: string;
  hasPerl: boolean;
  scriptPath: string;
}): string {
  const log = shellSingleQuote(opts.logPath);
  if (!opts.hasPerl) {
    return `cat >> ${log}`;
  }
  const script = shellSingleQuote(opts.scriptPath);
  return `perl ${script} >> ${log}`;
}

/** POSIX single-quote escaping: wrap in '…', and encode embedded quotes. */
function shellSingleQuote(value: string): string {
  return `'${value.replace(/'/g, `'\\''`)}'`;
}
